import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAppSettings } from '@/contexts/AppSettingsContext';

type GracePeriod = 'sticky' | 'normal' | 'big' | 'extended';

interface FeatureFlags {
  aiPartRecognition: boolean;
  sharedClaims: boolean;
  demoMode: boolean;
  debugMode: boolean;
  aiFeatures: boolean;
  
  // Parts Menu UX
  partsMenuGracePeriod: GracePeriod;
  partsMenuVerticalPath: boolean;
  
  allowDealerSpiffClaims: boolean;
  renderMockMappings: boolean;
  useMockNavigation: boolean;
  useDWProfiles: boolean;
  useMockDealerUsers: boolean;
}

interface FeatureFlagsContextType {
  flags: FeatureFlags;
  toggleFlag: (flag: keyof FeatureFlags) => void;
  setFlagValue: (flag: keyof FeatureFlags, value: any) => void;
  isFeatureEnabled: (flag: keyof FeatureFlags) => boolean;
  isLoading: boolean;
}

const defaultFlags: FeatureFlags = {
  aiPartRecognition: false,
  sharedClaims: false,
  demoMode: false,
  debugMode: false,
  aiFeatures: true,
  
  partsMenuGracePeriod: 'extended',
  partsMenuVerticalPath: false,
  
  allowDealerSpiffClaims: true,
  renderMockMappings: true,
  useMockNavigation: true,
  useDWProfiles: false,
  useMockDealerUsers: true,
};

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

export type { FeatureFlags, GracePeriod };

export const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { settings: appSettings, isLoading: settingsLoading } = useAppSettings();
  const [flags, setFlags] = useState<FeatureFlags>(defaultFlags);
  const [flagsLoading, setFlagsLoading] = useState(true);

  const isLoading = flagsLoading || settingsLoading;

  useEffect(() => {
    const fetchFlags = async () => {
      try {
        const flagsRes = await supabase.functions.invoke('get-feature-flags');
        
        if (flagsRes.error) {
          console.error('Error fetching feature flags:', flagsRes.error);
          return;
        }

        setFlags(prev => ({ ...defaultFlags, ...(flagsRes.data?.flags || {}) }));
      } catch (error) {
        console.error('Error fetching feature flags:', error);
      } finally {
        setFlagsLoading(false);
      }
    };

    fetchFlags();
  }, []);

  // Apply app_settings overrides when settings are ready
  useEffect(() => {
    if (!settingsLoading) {
      const gracePeriod = appSettings['flag_partsMenuGracePeriod'];
      if (gracePeriod) {
        setFlags(prev => ({ ...prev, partsMenuGracePeriod: gracePeriod as GracePeriod }));
      }
    }
  }, [appSettings, settingsLoading]);

  const toggleFlag = async (flag: keyof FeatureFlags) => {
    const newValue = !flags[flag];
    
    setFlags(prev => ({
      ...prev,
      [flag]: newValue
    }));

    try {
      const { error } = await supabase.functions.invoke('update-feature-flag', {
        body: { flagName: flag, enabled: newValue }
      });

      if (error) {
        console.error('Error updating feature flag:', error);
        setFlags(prev => ({
          ...prev,
          [flag]: !newValue
        }));
      }
    } catch (error) {
      console.error('Error updating feature flag:', error);
      setFlags(prev => ({
        ...prev,
        [flag]: !newValue
      }));
    }
  };

  const setFlagValue = async (flag: keyof FeatureFlags, value: any) => {
    const oldValue = flags[flag];
    
    setFlags(prev => ({
      ...prev,
      [flag]: value
    }));

    try {
      // For non-boolean flags, store as an app setting instead
      if (typeof value !== 'boolean') {
        const { error } = await supabase.functions.invoke('update-app-setting', {
          body: { key: `flag_${flag}`, value: String(value) }
        });
        if (error) {
          console.error('Error updating flag setting:', error);
          setFlags(prev => ({ ...prev, [flag]: oldValue }));
        }
      } else {
        const { error } = await supabase.functions.invoke('update-feature-flag', {
          body: { flagName: flag, enabled: value }
        });
        if (error) {
          console.error('Error updating feature flag:', error);
          setFlags(prev => ({ ...prev, [flag]: oldValue }));
        }
      }
    } catch (error) {
      console.error('Error updating feature flag:', error);
      setFlags(prev => ({
        ...prev,
        [flag]: oldValue
      }));
    }
  };

  const isFeatureEnabled = (flag: keyof FeatureFlags) => {
    return !!flags[flag];
  };

  return (
    <FeatureFlagsContext.Provider value={{ flags, toggleFlag, setFlagValue, isFeatureEnabled, isLoading }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};
